import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogOut, Download, RefreshCw, Inbox, Lock, Mail, MessageSquare, Building2, Phone, MapPin } from "lucide-react";
import { LOGO_DARK } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alba_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const LoginView = ({ onLogged }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Accesso effettuato.");
      onLogged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm rounded-[2rem] bg-white p-8 shadow-[0_30px_60px_rgba(0,0,0,0.3)]"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <img src={LOGO_DARK} alt="Alba Selection" className="h-20 w-auto" />
          <div className="mt-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
            <Lock className="h-3.5 w-3.5" /> Area riservata
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/60">Username</label>
            <input
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border border-navy/15 bg-cream/60 px-4 py-3 text-navy-deep outline-none transition-colors focus:border-gold focus:bg-white"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/60">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-navy/15 bg-cream/60 px-4 py-3 text-navy-deep outline-none transition-colors focus:border-gold focus:bg-white"
              autoComplete="current-password"
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-gold px-7 py-3.5 font-display text-base font-semibold text-navy-deep transition-colors hover:bg-amber hover:text-white disabled:opacity-60"
          >
            {loading ? "Accesso…" : "Accedi"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = () => localStorage.getItem(TOKEN_KEY);
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setLeads([]);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, authHeader());
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessione scaduta, effettua di nuovo l'accesso.");
        logout();
      } else {
        toast.error("Errore nel caricamento delle richieste.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (!token()) {
      setAuthed(false);
      return;
    }
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, authHeader());
        setAuthed(true);
        fetchLeads();
      } catch {
        logout();
      }
    })();
  }, [fetchLeads, logout]);

  const exportCsv = () => {
    const headers = ["Data", "Nome e cognome", "Cellulare", "Email", "Città", "Nome struttura", "Camere/unità", "Messaggio", "Privacy"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
      [fmtDate(l.created_at), l.nome, l.cellulare, l.email, l.citta, l.struttura, l.unita, l.messaggio, l.privacy ? "sì" : "no"].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alba-selection-lead-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authed === null) {
    return <div className="flex min-h-screen items-center justify-center bg-navy text-white/70">Caricamento…</div>;
  }
  if (!authed) {
    return <LoginView onLogged={() => { setAuthed(true); fetchLeads(); }} />;
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-10 border-b border-navy/10 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_DARK} alt="Alba Selection" className="h-10 w-auto" />
            <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-gold sm:block">Area richieste</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={fetchLeads}
              className="flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-medium text-navy transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
            <button
              data-testid="admin-export"
              onClick={exportCsv}
              disabled={!leads.length}
              className="flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy-deep transition-colors hover:bg-amber hover:text-white disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> CSV
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-medium text-navy transition-colors hover:border-red-400 hover:text-red-500"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-medium text-navy sm:text-4xl">Richieste ricevute</h1>
            <p className="mt-2 text-sm text-text-muted">
              {leads.length} {leads.length === 1 ? "richiesta" : "richieste"} dal form del sito.
            </p>
          </div>
        </div>

        {leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-navy/15 bg-white py-24 text-center">
            <Inbox className="h-12 w-12 text-navy/20" />
            <p className="mt-4 font-display text-lg text-navy">Ancora nessuna richiesta.</p>
            <p className="mt-1 text-sm text-text-muted">Le richieste dal form appariranno qui.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-2xl border border-navy/10 bg-white shadow-[0_8px_30px_rgba(31,58,95,0.05)] lg:block">
              <table className="w-full text-left text-sm" data-testid="admin-leads-table">
                <thead className="bg-navy text-white">
                  <tr>
                    <th className="px-5 py-3 font-medium">Data</th>
                    <th className="px-5 py-3 font-medium">Nome</th>
                    <th className="px-5 py-3 font-medium">Cellulare</th>
                    <th className="px-5 py-3 font-medium">Email</th>
                    <th className="px-5 py-3 font-medium">Città</th>
                    <th className="px-5 py-3 font-medium">Struttura / unità</th>
                    <th className="px-5 py-3 font-medium">Messaggio</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={l.id} data-testid={`admin-lead-row-${i}`} className="border-t border-navy/8 align-top hover:bg-cream/60">
                      <td className="whitespace-nowrap px-5 py-4 text-text-muted">{fmtDate(l.created_at)}</td>
                      <td className="px-5 py-4 font-semibold text-navy-deep">{l.nome}</td>
                      <td className="whitespace-nowrap px-5 py-4 text-navy-deep">{l.cellulare}</td>
                      <td className="px-5 py-4 text-navy-deep">{l.email}</td>
                      <td className="px-5 py-4 text-navy-deep">{l.citta}</td>
                      <td className="px-5 py-4 text-text-muted">
                        {l.struttura || "—"}{l.unita ? ` · ${l.unita}` : ""}
                      </td>
                      <td className="max-w-xs px-5 py-4 text-text-muted">{l.messaggio || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-4 lg:hidden">
              {leads.map((l, i) => (
                <div key={l.id} data-testid={`admin-lead-card-${i}`} className="rounded-2xl border border-navy/10 bg-white p-5 shadow-[0_8px_30px_rgba(31,58,95,0.05)]">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-lg font-semibold text-navy">{l.nome}</span>
                    <span className="text-xs text-text-muted">{fmtDate(l.created_at)}</span>
                  </div>
                  <div className="mt-3 space-y-2 text-sm">
                    <p className="flex items-center gap-2 text-navy-deep"><Phone className="h-4 w-4 text-gold" /> {l.cellulare}</p>
                    <p className="flex items-center gap-2 text-navy-deep"><Mail className="h-4 w-4 text-gold" /> {l.email}</p>
                    <p className="flex items-center gap-2 text-navy-deep"><MapPin className="h-4 w-4 text-gold" /> {l.citta}</p>
                    <p className="flex items-center gap-2 text-text-muted"><Building2 className="h-4 w-4 text-gold" /> {l.struttura || "—"}{l.unita ? ` · ${l.unita}` : ""}</p>
                    {l.messaggio ? (
                      <p className="flex items-start gap-2 text-text-muted"><MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {l.messaggio}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Admin;
